import { createAsyncThunk } from "@reduxjs/toolkit";

import * as checkoutAPI from "../../../api/checkout";
import * as cybersourceAPI from "../../../api/cybersource";
import { PaymentState } from "../../../constants";
import {
    ICybersourceTemplateContext,
    IPaymentState,
} from "../../../models/checkout";
import { IUser } from "../../../models/user";
import { objectKeys } from "../../../utils/functional";
import { IPayment, IReduxState } from "../reducers.interfaces";
import { serializeCheckoutError } from "../utils";
import {
    PAYMENT_REJECT_MSGS,
    buildCheckoutError,
    findDeclinedMethodType,
    forwardToThankYouPage,
    isOrderCompleteOrForwardable,
} from "./paymentStates";

/**
 * Arguments for completeOrderPayment thunk.
 */
interface CompleteOrderPaymentArgs {
    user: IUser | null;
}

/**
 * Get payment handlers for processing payment states.
 */
const getPaymentHandlers = (state: IReduxState, user: IUser | null) => {
    type IHandlers = {
        [t in IPayment["method_type"]]: (
            methodKey: string,
            paymentState: IPaymentState,
        ) => void;
    };
    const handlers: IHandlers = {
        "default": () => {
            throw buildCheckoutError(
                "payment",
                "Default method should never be pending",
            );
        },
        "cash": () => {
            throw buildCheckoutError(
                "payment",
                "Cash method should never be pending",
            );
        },
        "pay-later": () => {
            throw buildCheckoutError(
                "payment",
                "Pay Later method should never be pending",
            );
        },
        "financing": () => {
            throw buildCheckoutError(
                "payment",
                "Financing should never be pending",
            );
        },
        "cybersource": (methodKey, paymentState) => {
            const method = state.form.payment_methods[methodKey];
            if (!paymentState.required_action) {
                return;
            }
            if (paymentState.required_action.type !== "form") {
                return;
            }
            if (method.method_type !== "cybersource") {
                throw new Error("Payment error occurred");
            }
            const context: ICybersourceTemplateContext = {
                card_type: method.card_type,
                card_number: method.card_number,
                card_expiration: method.card_expiration,
                card_cvc: method.card_cvc,
                email: user ? user.email : state.form.guest_email,
                billing_first_name: state.form.billing_first_name,
                billing_last_name: state.form.billing_last_name,
                billing_phone_number: state.form.billing_phone_number,
                billing_line1: state.form.billing_line1,
                billing_line2: state.form.billing_line2,
                billing_line4: state.form.billing_line4,
                billing_postcode: state.form.billing_postcode,
                billing_state: state.form.billing_state,
                billing_country: state.form.billing_country,
            };
            const request = cybersourceAPI.renderRequestTemplate(
                context,
                paymentState.required_action,
            );
            cybersourceAPI.submitRequest(request);
        },
        "cybersource-microform": () => {
            // Microform payments are authorized during order creation via transient token.
            // This handler should never be reached.
            throw buildCheckoutError(
                "payment",
                "Microform payment should never be pending",
            );
        },
        "bluefin": () => {
            throw buildCheckoutError(
                "payment",
                "Bluefin should never be pending",
            );
        },
    };
    return handlers;
};

/**
 * Complete order payment after initial order placement.
 *
 * This thunk handles the payment state checking and authorization flow
 * after an order has been placed. It is called:
 * 1. After initial placeOrder to check payment states
 * 2. After Cybersource 3DS iframe completes to finalize payment
 *
 * Unlike placeOrder, this does NOT create a new order - it only checks
 * existing payment states and handles authorization flows.
 *
 * @param {IUser | null} args.user - Current user (or null for guest checkout)
 * @returns {Promise<void>} Promise resolving when payment is complete or flow initiated
 *
 * @throws {ICheckoutHTTPError} If payment is declined
 *
 * @sideEffects
 * - Makes API call to check payment states via checkoutAPI.checkPaymentStates
 * - Updates `data.payment_states` via updatePaymentStates action
 * - May trigger Cybersource 3DS flow via cybersourceAPI.submitRequest
 * - May redirect to thank you page via forwardToThankYouPage
 */
export const completeOrderPayment = createAsyncThunk(
    "checkout/completeOrderPayment",
    async (
        args: CompleteOrderPaymentArgs,
        { getState, dispatch },
    ): Promise<void> => {
        const state = getState() as { checkout: IReduxState };
        const checkoutState = state.checkout;

        // Check payment states and handle authorization
        const handlers = getPaymentHandlers(checkoutState, args.user);
        const states = await checkoutAPI.checkPaymentStates();

        // Import actions dynamically to avoid circular dependency
        const { updatePaymentStates } = await import("../reducers");
        // Write the states to redux on every outcome,
        // we were losing the GA purchase events (#35199).
        dispatch(updatePaymentStates(states));

        if (isOrderCompleteOrForwardable(states)) {
            forwardToThankYouPage();
            return;
        }

        // Check for decline
        const declinedMethodType = findDeclinedMethodType(
            states,
            checkoutState.form.payment_methods,
        );
        if (declinedMethodType) {
            throw buildCheckoutError(
                "global",
                PAYMENT_REJECT_MSGS[declinedMethodType],
            );
        }

        let highestAmount: number | null = null;
        let nextMethodKeyToCharge: string | null = null;

        // Track highest pending amount
        objectKeys(states.payment_method_states).forEach((key) => {
            const paymentMethodState = states.payment_method_states[key];
            if (
                paymentMethodState.status === PaymentState.PENDING &&
                (highestAmount === null ||
                    Number(paymentMethodState.amount) > highestAmount)
            ) {
                nextMethodKeyToCharge = key;
                highestAmount = Number(paymentMethodState.amount);
            }
        });

        // Try highest pending amount
        if (nextMethodKeyToCharge) {
            const paymentMethodState =
                states.payment_method_states[nextMethodKeyToCharge];
            const paymentMethodType =
                checkoutState.form.payment_methods[nextMethodKeyToCharge]
                    .method_type;
            const handler = handlers[paymentMethodType];
            handler(nextMethodKeyToCharge, paymentMethodState);
        }
    },
    { serializeError: serializeCheckoutError },
);
